import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { CLIENT_ID } from '../env';
import { deepMerge } from '@/utils/deepMerge';

// Import translation files
import enTranslations from './locales/en.json';

// ============================================================================
// LANGUAGE CONFIGURATION - SINGLE PLACE TO MANAGE ALL LANGUAGES
// ============================================================================

// Language enum - ADD NEW LANGUAGES HERE ONLY
export enum Language {
  EN = 'en',
  // To add new language: FRENCH = 'fr', GERMAN = 'de', etc.
}

// Default language - CHANGE HERE TO SET DEFAULT
export const DEFAULT_LANGUAGE: Language = Language.EN;

// Auto-generated from enum - DO NOT MODIFY
export const SUPPORTED_LANGUAGES = Object.values(Language) as Language[];
export type SupportedLanguage = Language;

// ============================================================================
// AUTO-GENERATED CONFIGURATION - DO NOT MODIFY BELOW
// ============================================================================

/**
 * Load client-specific translations and merge with default translations
 * @param defaultTranslations - Default translations object
 * @param clientId - Current client identifier
 * @param language - Language code (e.g., 'en', 'fr')
 * @returns Merged translations object
 */
const loadClientTranslations = async (
  defaultTranslations: any,
  clientId: string | undefined,
  language: string
) => {
  if (!clientId) {
    return defaultTranslations;
  }

  try {
    // Try to load client-specific translation file
    const clientTranslations = await import(
      `./locales/clients/${clientId.toLowerCase()}/${language}.json`
    );

    // Deep merge client translations with default translations
    // Client translations will override default ones while preserving nested structure
    return deepMerge(defaultTranslations, clientTranslations.default);
  } catch (error) {
    // Client translation file doesn't exist, return default translations
    console.log(
      `No client-specific translations found for ${clientId}/${language}. Using default translations.`
    );
    return defaultTranslations;
  }
};

/**
 * Get resources with client-specific overrides
 * @param language - Language code
 * @returns Resources object for i18next
 */
const getResources = async (language: string = DEFAULT_LANGUAGE) => {
  const defaultTranslations = {
    [Language.EN]: { translation: enTranslations },
    // New languages will be added here automatically when you:
    // 1. Add to Language enum above
    // 2. Create corresponding JSON file
    // 3. Import the JSON file at top
  };

  // Get client-specific translations and merge
  const mergedTranslations = await loadClientTranslations(
    defaultTranslations[language as Language]?.translation || enTranslations,
    CLIENT_ID,
    language
  );

  return {
    ...defaultTranslations,
    [language]: { translation: mergedTranslations },
  };
};

// Resources object for i18next - will be updated with client overrides
const resources = {
  [Language.EN]: { translation: enTranslations },
  // New languages will be added here automatically when you:
  // 1. Add to Language enum above
  // 2. Create corresponding JSON file
  // 3. Import the JSON file at top
};

// ============================================================================
// PROGRAMMATIC LANGUAGE CHANGE FUNCTION
// ============================================================================

/**
 * Load and apply client-specific translations for a given language
 * @param language - Language to load client translations for
 */
const loadAndApplyClientTranslations = async (language: Language) => {
  if (!CLIENT_ID) return;

  try {
    const mergedResources = await getResources(language);
    // Update resources with client overrides
    Object.keys(mergedResources).forEach((lng) => {
      const resource = mergedResources[lng as Language];
      if (resource) {
        i18n.addResourceBundle(
          lng,
          'translation',
          resource.translation,
          true,
          true
        );
      }
    });
  } catch (error) {
    console.log('Failed to load client translations:', error);
  }
};

// Function to change language programmatically (call this later when needed)
export const setDefaultLanguage = async (language: Language): Promise<void> => {
  // Load client-specific translations for the new language
  await loadAndApplyClientTranslations(language);

  // Change language
  i18n.changeLanguage(language);
};

// Initialize i18next SYNCHRONOUSLY with default translations
// This ensures translations are available immediately on page load/refresh
i18n.use(initReactI18next).init({
  resources,
  fallbackLng: DEFAULT_LANGUAGE,
  lng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

// Load client overrides in background (non-blocking) for initial load
loadAndApplyClientTranslations(DEFAULT_LANGUAGE);

export default i18n;
